<template>
  <FullPage class="label-screen">
    <template #topbar-more>
      <BaseButton
        class="context-action"
        kind="auxiliary"
        text="Open"
        is-expanded
        @click="openLabel"
      >
        <template #icon>
          <OpenIcon />
        </template>
      </BaseButton>
      <BaseButton
        class="context-action"
        kind="auxiliary"
        text="Copy link"
        is-expanded
        @click="copyLink"
      >
        <template #icon>
          <CopyLinkIcon />
        </template>
      </BaseButton>
      <BaseButton
        class="context-action"
        kind="auxiliary"
        text="Delete"
        tint="red"
        is-expanded
        @click="deleteLabel"
      >
        <template #icon>
          <DeleteIcon />
        </template>
      </BaseButton>
    </template>
    <div class="label-header">
      <ColorsButton
        :value="labelColor"
        placement="bottom-start"
        @select="onSelectLabelColor"
      />
      <GraphTitle
        class="label-title"
        :data="title"
        state="title"
        :name="titleFieldName"
      />
    </div>

    <div class="tabs">
      <BaseButton
        v-for="tab in tabsDef"
        :key="tab.name"
        kind="auxiliary"
        :text="tab.name"
        :class="{ selected: selectedTab === tab.name }"
        @click="setTab(tab.name)"
      >
        <template #icon>
          <component :is="tab.icon" />
        </template>
      </BaseButton>
    </div>

    <div class="label-view">
      <View
        v-if="viewQuery.current"
        v-model:query="viewQuery"
        :workspace-id="workspaceId"
        :storage-key-prefix="`labels/${labelId}/${selectedTab}`"
        :parameters="{ configParameters: { datasource: false, delete: false } }"
        :gated="false"
      />
    </div>
  </FullPage>
</template>

<script lang="ts">
import {
  Component,
  computed,
  defineComponent,
  onMounted,
  ref,
  toRef,
  watch,
} from "vue";
import { useRouter } from "vue-router";

import type {
  CalendarColor,
  Controller,
  GraphDatabaseDictionary,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import BaseButton from "@/components/base/BaseButton.vue";
import { persistLabelColor, resolveLabelColor } from "@/components/color";
import ColorsButton from "@/components/ColorsButton.vue";
import GraphTitle from "@/components/graph/properties/GraphTitle.vue";
import { buildLabelFilterQuery } from "@/components/graph/query";
import { useGraphObject } from "@/components/graph/useGraphObject";
import CopyLinkIcon from "@/components/icons/CopyLinkIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import EventIcon from "@/components/icons/EventIcon.vue";
import OpenIcon from "@/components/icons/OpenIcon.vue";
import PageIcon from "@/components/icons/PageIcon.vue";
import PersonIcon from "@/components/icons/PersonIcon.vue";
import TaskIcon from "@/components/icons/TaskIcon.vue";
import * as inject from "@/components/inject";
import { LABELS_TABLE_ID } from "@/components/tables";
import FullPage from "@/components/templates/FullPage.vue";
import { getDefaultViewConfigForDataSource } from "@/components/views/config/default";
import View, { type Query } from "@/components/views/View.vue";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { browser } from "@/services/browser";
import {
  navigateToCustomObject,
  navigateToHome,
  resolveRouteFullPath,
} from "@/utils/navigate";
import { deleteCustomObject } from "@/utils/object";

type LabelTab = "Tasks" | "Events" | "Pages" | "Contacts";

/**
 * The initial query of a tab's view: the label filter, with the default
 * visible fields of the tab's data source when the parsed view has none.
 */
const buildLabelInitialQuery = async (
  controller: Controller,
  wsId: WorkspaceId,
  databases: GraphDatabaseDictionary | undefined,
  typeName: string,
  labelId: string
): Promise<string> => {
  const rawQuery = buildLabelFilterQuery(typeName, labelId);
  const parsed = await controller.graph.view.ofQuery(wsId, rawQuery);
  if ("error" in parsed) return rawQuery;
  const db = Object.values(databases ?? {}).find((d) => d.name === typeName);
  if (!db) return rawQuery;
  const defaults = await getDefaultViewConfigForDataSource(
    controller,
    wsId,
    db
  );
  const config = {
    ...parsed.value,
    visible_fields: parsed.value.visible_fields ?? defaults.visible_fields,
  };
  return controller.graph.view.toQuery(config);
};

export default defineComponent({
  name: "LabelScreen",
  components: {
    BaseButton,
    ColorsButton,
    CopyLinkIcon,
    DeleteIcon,
    EventIcon,
    FullPage,
    GraphTitle,
    OpenIcon,
    PageIcon,
    PersonIcon,
    TaskIcon,
    View,
  },
  props: {
    labelId: { type: String, required: true },
  },
  async setup(props) {
    const labelIdRef = toRef(props, "labelId");

    const router = useRouter();
    const controller = inject.controller();
    const profile = inject.profile();
    const workspaceDatabases = inject.workspaceDatabases();
    const logger = inject.logger();
    const simpleModal = inject.simpleModal();
    const menu = inject.menu();

    const workspaceId = computed(() => profile.value.user.personal.workspaceId);
    const databaseId = computed(() => LABELS_TABLE_ID);
    const mode = computed<"autosave">(() => "autosave");

    const tabsTitles: LabelTab[] = ["Tasks", "Events", "Pages", "Contacts"];
    const selectedTab = ref<LabelTab>(tabsTitles[0]);

    const browserStore = useBrowserStore(profile.value.user.accountId);
    const { namespacedKey } = browserStore;
    const storageKey = "label:tab";

    const setTab = (tab: LabelTab) => {
      selectedTab.value = tab;
      browser.store.set(namespacedKey(storageKey), tab);
    };

    onMounted(() => {
      const stored = browser.store.get<unknown>(
        namespacedKey(storageKey),
        selectedTab.value
      );
      const storedTab = tabsTitles.find((tab) => tab === stored);
      if (storedTab) selectedTab.value = storedTab;
    });

    const releaser = useReleaser();
    const { title, titleFieldName } = await releaser.add(
      useGraphObject(
        controller,
        workspaceDatabases,
        workspaceId,
        databaseId,
        labelIdRef,
        mode
      )
    );

    const labelColor = computed(() =>
      resolveLabelColor(browserStore, props.labelId, title.value || "")
    );
    const onSelectLabelColor = (color: CalendarColor) => {
      persistLabelColor(browserStore, props.labelId, color);
    };

    const tabConfig: Record<LabelTab, { typeName: string; icon: Component }> = {
      Tasks: { typeName: "tasks", icon: TaskIcon },
      Events: { typeName: "events", icon: EventIcon },
      Pages: { typeName: "pages", icon: PageIcon },
      Contacts: { typeName: "people", icon: PersonIcon },
    };

    const tabsDef = tabsTitles.map((name) => ({
      name,
      icon: tabConfig[name].icon,
    }));

    const viewQueryStorageKey = (tab: LabelTab, labelId: string) =>
      namespacedKey(`labels/${labelId}/${tab}:query`);

    const buildQueryRecord = async (
      wsId: string,
      tab: LabelTab,
      labelId: string
    ): Promise<Query> => {
      const databases = workspaceDatabases.value?.[wsId];
      const initial = await buildLabelInitialQuery(
        controller,
        wsId,
        databases,
        tabConfig[tab].typeName,
        labelId
      );
      const stored = browser.store.get<unknown>(
        viewQueryStorageKey(tab, labelId),
        ""
      );
      return {
        initial,
        current: typeof stored === "string" && stored ? stored : initial,
      };
    };

    const viewQuery = ref<Query>(
      await buildQueryRecord(
        workspaceId.value,
        selectedTab.value,
        props.labelId
      )
    );

    releaser.defer(
      watch([selectedTab, () => props.labelId], async ([tab, id]) => {
        viewQuery.value = await buildQueryRecord(workspaceId.value, tab, id);
      })
    );

    releaser.defer(
      watch(
        () => viewQuery.value.current,
        (current) => {
          browser.store.set(
            viewQueryStorageKey(selectedTab.value, props.labelId),
            current
          );
        }
      )
    );

    const openLabel = () => {
      navigateToCustomObject(
        router,
        workspaceId.value,
        LABELS_TABLE_ID,
        props.labelId
      );
    };

    const copyLink = async () => {
      const path = resolveRouteFullPath(router, {
        name: RouteNames.Object,
        params: {
          workspaceId: workspaceId.value,
          tableId: LABELS_TABLE_ID,
          rowId: props.labelId,
        },
      });
      const url = `https://open.routine.co?path=${path}`;
      try {
        await copy(url);
        controller.notifications.notify("Link copied to clipboard");
      } catch {
        controller.notifications.notify("Failed to copy link");
      }
    };

    const deleteLabel = () => {
      simpleModal.value.showModal({
        header: "Delete label",
        text: "Are you sure you want to delete this label?",
        actions: [
          {
            name: "Delete",
            icon: DeleteIcon,
            type: "primary",
            func: async () => {
              await navigateToHome(router, menu);
              await deleteCustomObject(
                controller,
                logger,
                profile,
                workspaceId.value,
                LABELS_TABLE_ID,
                props.labelId
              );
            },
          },
        ],
        withCancelButton: true,
      });
    };

    return {
      copyLink,
      deleteLabel,
      labelColor,
      onSelectLabelColor,
      openLabel,
      selectedTab,
      setTab,
      tabsDef,
      title,
      titleFieldName,
      viewQuery,
      workspaceId,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/context-actions.scss";

.label-screen {
  .label-header {
    display: flex;
    align-items: center;
    column-gap: 10px;
  }

  .label-title {
    max-width: fit-content;

    :deep(.r-input .input),
    :deep(.r-input .placeholder) {
      @extend %font-h2;
    }
  }

  .tabs {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 8px;
    margin-inline-start: -4px;

    :deep(button.base-button.none.auxiliary.default.selected),
    :deep(button.base-button.none.auxiliary.default:active) {
      @include themed() {
        background-color: t($grey-05);
        color: t($primary);
      }
    }

    :deep(button.base-button.none.auxiliary.default) {
      @include themed() {
        color: t($grey-50);
      }

      &:hover:not(.selected) {
        @include themed() {
          color: t($grey-80);
        }
      }
    }

    .tab-count {
      @extend %font-caption-regular;

      @include themed() {
        color: t($grey-40);
      }
    }
  }

  .label-view {
    flex: 1;
    overflow: auto;
  }
}
</style>
